/**
 * =============================================================================
 * ROASTBREW — COURSES PAGE (Galaxy Navigator v3)
 * =============================================================================
 *
 * Pure flexbox + CSS. No canvas, no RAF, no ResizeObserver, no imperative DOM.
 * Hot-loaded columns — content stays mounted, fades via CSS, no blank flashes.
 * Left-side back navigation. Completion states at every level.
 *
 * 5-layer: Subjects → Courses → Chapters → Sections → Items
 * =============================================================================
 */

import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { isLocked } from './config';
import { videoCompleted, videoPct, nodePct, nodeCompleted } from './data/progress';
import UpgradePrompt from './UpgradePrompt';

// Small lock glyph for tier-gated (locked) nodes.
function lockMark(color) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" style={{ flexShrink: 0 }}>
      <rect x="5" y="11" width="14" height="9" rx="1.5"/>
      <path d="M8 11V8a4 4 0 0 1 8 0v3" strokeLinecap="round"/>
    </svg>
  );
}

/* ── URL <-> tree helpers ──
   The Courses URL mirrors the breadcrumb:
     /courses/{subject}/{course}/{chapter}/{section}/{topic}
   Each segment is a slug of the node name. Matching is tolerant (case- and
   separator-insensitive) so "Algebra2", "algebra-2", and "algebra 2" all
   resolve to the same node. A path that reaches a topic deep-links to its video. */
function slugKey(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '');
}
// Match a URL segment to a node by its server-provided `slug` (tolerant of case
// and separators, so "Algebra2", "algebra-2", "algebra 2" all resolve).
function findBySlug(list, seg) {
  var k = slugKey(seg);
  return (list || []).find(function (n) { return slugKey(n.slug) === k; }) || null;
}
// Resolve URL segments to selected node ids (+ a topic node, if the path reaches one).
function resolvePath(segs, subjects) {
  var out = { subId: null, crsId: null, chId: null, secId: null, topic: null };
  if (!segs || !segs.length) return out;
  var sub = findBySlug(subjects, segs[0]); if (!sub) return out; out.subId = sub.id;
  if (segs[1] == null) return out;
  var crs = findBySlug(sub.courses, segs[1]); if (!crs) return out; out.crsId = crs.id;
  if (segs[2] == null) return out;
  var ch = findBySlug(crs.chapters, segs[2]); if (!ch) return out; out.chId = ch.id;
  if (segs[3] == null) return out;
  var sec = findBySlug(ch.sections, segs[3]); if (!sec) return out; out.secId = sec.id;
  if (segs[4] == null) return out;
  var top = findBySlug(sec.topics, segs[4]); if (top) out.topic = top;
  return out;
}
// Build a canonical /courses path from an ordered list of nodes (uses each node's slug).
function coursesPath(nodes) {
  return '/courses' + (nodes || []).map(function (n) { return '/' + (n && n.slug ? n.slug : ''); }).join('');
}

/* ═══════════════════════════════════════════════════════════════
   SHARED VISUAL LANGUAGE
   ───────────────────────────────────────────────────────────────
   Level 0 was a celestial body — sphere, glow, hairline ring, bob — and levels
   1-4 were rectangles with a 7px dot. The metaphor was announced and then
   abandoned, which reads worse than never promising it.
   The fix is ONE MATERIAL, DIFFERENT FORMS. A subject orb carries a one-word
   label; a chapter carries "Adding & subtracting polynomials". Text cannot live
   inside a circle at that length, so the circle stays and the text moves beside
   it: medallions at level 1, nodes on an orbital spine at 2-3, satellite tokens
   at level 4. Same gradient core, same glow, same hairline ring throughout.
   ═══════════════════════════════════════════════════════════════ */

// Progress as an ARC on the object itself, replacing the "42%" caption. Stroke is
// drawn from 12 o'clock via a -90deg rotation; dashoffset is the unfilled part.
function Ring({ size, pct, color, track, width }) {
  var w = width || 2;
  var r = (size - w) / 2;
  var C = 2 * Math.PI * r;
  return (
    <svg className="gx-arc" width={size} height={size} viewBox={'0 0 ' + size + ' ' + size} aria-hidden="true">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={track} strokeWidth={w} />
      {pct > 0 && (
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={w}
          strokeDasharray={C} strokeDashoffset={C * (1 - Math.min(100, pct) / 100)}
          strokeLinecap="round" transform={'rotate(-90 ' + (size/2) + ' ' + (size/2) + ')'} />
      )}
    </svg>
  );
}

// U+2713 has the same problem as U+25B6 — iOS can route it to the emoji font, so
// the tick renders at an unpredictable size and baseline on a phone while looking
// fine on desktop. One SVG, used everywhere a check is shown.
function tick(color, size) {
  return (
    <svg width={size||10} height={size||10} viewBox="0 0 12 12" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 6.4 L4.6 9 L10 3.2"/>
    </svg>
  );
}

var ROMAN = ['','I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI','XVII','XVIII','XIX','XX'];
function roman(n) { return ROMAN[n] || String(n); }


/* ═══════════════════════════════════════════════════════════════
   SUBJECT LEVEL TOGGLE
   ───────────────────────────────────────────────────────────────
   With one or two subjects, an opening screen holding a single orb is a rung on
   the ladder that costs a tap and shows nothing. Set false and the tree opens on
   EVERY COURSE FROM EVERY SUBJECT instead, with the subject name carried as each
   medallion's label so nothing is lost.

   TO REVERT: set this to true. That is the only change required.

   URLs are deliberately UNTOUCHED either way — they remain
   /courses/:subject/:course/:chapter/:section, because resolvePath needs the
   subject slug to resolve a course and every existing link must keep working.
   Only the DISPLAY skips a rung: the subjects column is not rendered, the track
   is four columns instead of five, and viewDepth is depth-1. Nothing about
   routing, resolution, caching or deep-linking changes.
   ═══════════════════════════════════════════════════════════════ */
const SHOW_SUBJECT_LEVEL = false;

// Column indices inside the track, derived from the flag so there is one source
// of truth for both the shift and the per-column visibility gate.
const COL = SHOW_SUBJECT_LEVEL
  ? { subjects: 0, courses: 1, chapters: 2, sections: 3, topics: 4 }
  : { subjects: -1, courses: 0, chapters: 1, sections: 2, topics: 3 };
const COL_COUNT = SHOW_SUBJECT_LEVEL ? 5 : 4;
const COL_NAMES = SHOW_SUBJECT_LEVEL
  ? ['Subjects', 'Courses', 'Chapters', 'Sections', 'Topics']
  : ['Courses', 'Chapters', 'Sections', 'Topics'];

/* ═══════════════════════════════════════════════════════════════ */

export default function CoursesPage({
  // isDesktop and sidebarOpen are accepted but intentionally UNUSED. AppLayout clones
  // all four responsive props onto every page; this one derives its layout from
  // CONTAINER percentages, so it needs neither the desktop flag nor the sidebar state.
  // eslint-disable-next-line no-unused-vars
  isMobile = false, isTablet = false, isDesktop = true, sidebarOpen = true,
}) {
  // Catalog drives navigation; read it from context. It now starts EMPTY rather
  // than seeded with placeholder data, so `subjects` is legitimately [] until the
  // fetch lands — `catalogLoading` covers that, and a total failure is handled
  // app-wide by CatalogErrorOverlay rather than here.
  const { catalog, catalogLoading, profile, progressByVideo } = useAuth();
  var currentTier = (profile && profile.currentTier) || 'free';
  const subjects = (catalog && catalog.length > 0) ? catalog : [];
  const loading = catalogLoading;
  const navigate = useNavigate();
  const { colors, isLight } = useTheme();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  var ac = colors.accent;
  var gl = colors.accentGlow;

  // Selection is derived from the URL (single source of truth) so any tree
  // position is shareable and the browser back/forward buttons walk the tree.
  const { pathname } = useLocation();
  var segs = useMemo(function () {
    return pathname.replace(/^\/+|\/+$/g, '').split('/').slice(1)
      .map(function (s) { try { return decodeURIComponent(s); } catch (e) { return s; } })
      .filter(Boolean);
  }, [pathname]);
  var resolved = useMemo(function () { return resolvePath(segs, subjects); }, [segs, subjects]);

  var selSub = resolved.subId;
  var selCrs = resolved.crsId;
  var selCh = resolved.chId;
  var selSec = resolved.secId;
  var depth = selSec ? 4 : selCh ? 3 : selCrs ? 2 : selSub ? 1 : 0;
  // viewDepth is which COLUMN is on screen. With the subject level hidden, a bare
  // /courses and a subject-only /courses/mathematics both resolve to the same
  // opening view (all courses), so depth 0 and 1 collapse to viewDepth 0.
  var viewDepth = SHOW_SUBJECT_LEVEL ? depth : Math.max(0, depth - 1);

  // Data lookups
  var subD = selSub ? subjects.find(function(s) { return s.id === selSub }) : null;
  var crsD = subD && selCrs ? (subD.courses || []).find(function(c) { return c.id === selCrs }) : null;
  var chD = crsD && selCh ? (crsD.chapters || []).find(function(c) { return c.id === selCh }) : null;
  var secD = chD && selSec ? (chD.sections || []).find(function(s) { return s.id === selSec }) : null;

  // Every course across every subject, each paired with its OWN subject so
  // coursesPath([subject, course]) still produces the canonical URL. Only built
  // when the subject level is hidden.
  var allCourses = useMemo(function() {
    if (SHOW_SUBJECT_LEVEL) return [];
    var out = [];
    (subjects || []).forEach(function(su) {
      (su.courses || []).forEach(function(c) { out.push({ subject: su, course: c }); });
    });
    return out;
  }, [subjects]);

  // Hot-load caches — keep last valid data so columns don't go blank
  var crsCache = useRef([]);
  var chCache = useRef([]);
  var secCache = useRef([]);
  var itmCache = useRef(null);
  if (subD) crsCache.current = subD.courses || [];
  if (crsD) chCache.current = crsD.chapters || [];
  if (chD) secCache.current = chD.sections || [];
  if (secD) itmCache.current = secD;

  // Navigation
  var goBack = useCallback(function() {
    if (selSec) navigate(coursesPath([subD, crsD, chD]));
    else if (selCh) navigate(coursesPath([subD, crsD]));
    else if (selCrs) navigate(SHOW_SUBJECT_LEVEL ? coursesPath([subD]) : '/courses');
    else if (selSub) navigate('/courses');
  }, [selSub, selCrs, selCh, selSec, subD, crsD, chD, navigate]);

  useEffect(function() {
    var fn = function(e) { if (e.key === 'Escape') goBack(); };
    document.addEventListener('keydown', fn);
    return function() { document.removeEventListener('keydown', fn); };
  }, [goBack]);   // was missing a dep array, so it re-bound its listener every render

  // A URL that reaches a topic deep-links straight into that topic's single video.
  var topicVideoId = resolved.topic ? resolved.topic.videoId : null;
  useEffect(function() {
    if (topicVideoId) {
      navigate('/video/' + topicVideoId + (selCrs ? ('?course=' + selCrs) : ''), { replace: true });
    }
  }, [topicVideoId, selCrs, navigate]);

  // ── Layout ────────────────────────────────────────────────────────────────
  // colW is a PERCENTAGE OF THE CONTAINER, not of the viewport.
  //
  // It used to be `vw`, and that is the bug behind cards appearing to float off the
  // page. This renders inside AppLayout's <main>, which carries marginLeft:280px
  // while the desktop sidebar is open — so the usable box is (100vw - 280px) while
  // every column width AND the track's translateX were measured against the FULL
  // viewport. On a 1280px screen with the sidebar open a "36vw" column is 461px,
  // i.e. 46% of the 1000px it actually has, and the error compounds at every depth.
  //
  // `.gx` is width:100%, so percentages are correct automatically and the sidebar
  // needs no special-casing.
  //
  // Mobile is now a FULL-width column: at 88vw, 12vw of the NEXT column showed at
  // the right edge, which is the other half of the "floating off the page" report.
  var colW = isMobile ? 100 : isTablet ? 52 : 36;
  var gap = isMobile ? 14 : isTablet ? 20 : 28;
  var cardPad = isMobile ? '10px 14px' : isTablet ? '10px 14px' : '12px 18px';
  // Label names the level you are RETURNING to. Derived from a column-name list
  // built off the flag, so it cannot drift out of sync with COL / COL_COUNT.
  var backLabel = viewDepth > 0 ? COL_NAMES[viewDepth - 1] : '';

  // Theme
  var cardBg = isLight ? 'rgba(240,239,234,0.75)' : ac + '0f';
  var cardBd = isLight ? 'rgba(0,0,0,0.06)' : ac + '1a';
  var secBg = isLight ? 'rgba(240,239,234,0.55)' : ac + '08';
  var secBd = isLight ? 'rgba(0,0,0,0.04)' : ac + '12';
  var panelBg = isLight ? 'rgba(245,244,240,0.82)' : ac + '0d';
  var panelBd = isLight ? 'rgba(0,0,0,0.05)' : ac + '17';
  var hoverBg = isLight ? 'rgba(0,0,0,0.025)' : ac + '0a';
  var tagBg = isLight ? 'rgba(0,0,0,0.04)' : ac + '14';
  var doneTint = ac + '08';
  var doneBorder = ac;
  var nebula = isLight
    ? 'radial-gradient(ellipse at 35% 40%,'+ac+'18 0%,transparent 55%),radial-gradient(ellipse at 65% 65%,'+ac+'0d 0%,transparent 50%)'
    : 'radial-gradient(ellipse at 30% 35%,'+ac+'16 0%,transparent 50%),radial-gradient(ellipse at 60% 60%,'+ac+'0c 0%,transparent 45%)';
  var orbColors = [ac, ac+'dd', ac+'bb', ac+'99'];
  var orbGlows = isLight ? [ac+'48',ac+'34',ac+'42',ac+'28'] : [ac+'72',ac+'4d',ac+'5c',ac+'38'];
  var orbSize = isMobile ? 64 : isTablet ? 80 : 100;
  // One size ladder for the whole family: body > medallion > node > sub-node > token.
  var medSize = isMobile ? 52 : isTablet ? 58 : 66;
  var nodeSize = isMobile ? 30 : isTablet ? 32 : 34;
  var subNodeSize = isMobile ? 22 : 24;
  var tokSize = isMobile ? 24 : 22;
  var triSize = isMobile ? 10 : 9;

  // Breadcrumbs
  var crumbs = useMemo(function() {
    if (!selSub) return null;
    var sub = subjects.find(function(s) { return s.id === selSub });
    if (!sub) return null;
    // With the subject level hidden the root crumb is the course list itself, and
    // the subject name becomes non-navigable context rather than a destination
    // (there is no subject-only view to go to).
    var a = SHOW_SUBJECT_LEVEL
      ? [
          { l:'Subjects', fn:function(){navigate('/courses')} },
          { l:sub.name, cur:!selCrs, fn:function(){navigate(coursesPath([sub]))} },
        ]
      : [
          { l:'Courses', fn:function(){navigate('/courses')} },
          { l:sub.name, cur:!selCrs },
        ];
    if (selCrs) {
      var crs = (sub.courses||[]).find(function(c){return c.id===selCrs});
      if (crs) { a.push({l:crs.name,cur:!selCh,fn:function(){navigate(coursesPath([sub,crs]))}});
        if (selCh) {
          var ch = (crs.chapters||[]).find(function(c){return c.id===selCh});
          if (ch) { a.push({l:ch.name,cur:!selSec,fn:function(){navigate(coursesPath([sub,crs,ch]))}});
            if (selSec) {
              var sec = (ch.sections||[]).find(function(s){return s.id===selSec});
              if (sec) a.push({l:sec.name,cur:true});
    }}}}} return a;
  }, [selSub,selCrs,selCh,selSec,subjects,navigate]);

  // While a topic URL redirects to the player, render a calm placeholder
  // (avoids a one-frame flash of the section sliding in behind it).
  if (resolved.topic) {
    return <div style={{ width: '100%', height: '100dvh', background: colors.bg }} />;
  }

  return (
    <div className="gx" style={{background:colors.bg,
        // reserve room for the back rail only while it exists (depth > 0)
        ['--gx-gutter']: viewDepth > 0 ? (isMobile ? '56px' : '76px') : '0px'}}>
      {/* The nebula drifts and tightens as you descend, so going deeper reads as
          travelling inward rather than sliding sideways. Transform only — no layout. */}
      <div style={{position:'absolute',inset:'-10%',background:nebula,pointerEvents:'none',zIndex:0,
        opacity:0.6-depth*0.06,
        transform:'translateX('+(-depth*3)+'%) scale('+(1+depth*0.06)+')',
        transition:'transform 1.6s cubic-bezier(0.4,0,0.15,1),opacity 1.2s ease'}}/>

      {/* ── Track ── */}
      {/* Track is 5 columns wide as a % OF THE CONTAINER (5 * colW). Each column is
          therefore 20% OF THE TRACK, and translateX — which resolves against the
          element's OWN width — is a clean -depth * 20%, independent of colW. */}
      <div className="gx-track" style={{width:(COL_COUNT*colW)+'%',transform:'translateX('+(-viewDepth*(100/COL_COUNT))+'%)'}}>

        {/* Col 0: Subjects — SCAFFOLDING. Kept whole; flip SHOW_SUBJECT_LEVEL to
            restore it. Rendered as null rather than hidden so it contributes no
            width to the track when off. */}
        {SHOW_SUBJECT_LEVEL && (
        <div className="gx-col" style={{width:(100/COL_COUNT)+'%'}}>
         <div className="gx-colinner" style={{gap:gap}}>
          {subjects.map(function(s,i){
            var sel=selSub===s.id; var dim=selSub&&!sel;
            var oc=orbColors[i%4]; var og=orbGlows[i%4];
            var sPct=nodePct(s, progressByVideo); var sDone=nodeCompleted(s, progressByVideo);
            var sz=orbSize+sPct*0.5;
            return (
              <div key={s.id} className={'gx-orb'+(sel?' gx-orb--sel':'')+(sDone?' gx-orb--done':'')}
                onClick={function(){navigate(selSub===s.id?'/courses':coursesPath([s]))}}
                style={{width:sz,height:sz,opacity:dim?0.12:1,animationDelay:(i*0.2)+'s',background:'radial-gradient(circle at 35% 35%,'+oc+'20,'+oc+'08,transparent 70%)'}}>
                <div className="gx-orb__glow" style={{background:og}}/>
                <div className="gx-orb__ring" style={{borderColor:isLight?'rgba(0,0,0,0.06)':ac+'1f'}}/>
                {sDone&&<div className="gx-orb__check" style={{background:ac}}>{tick(colors.bg,9)}</div>}
                {/* Progress is now part of the body, not a caption under it. */}
                <Ring size={sz} pct={sPct} color={oc} track={isLight?'rgba(0,0,0,0.05)':ac+'14'} width={isMobile?2:2.5}/>
                <span className="gx-orb__sym" style={{color:oc,fontSize:isMobile?15:isTablet?18:21}}>{s.symbol||'\u25CF'}</span>
                <div className="gx-orb__name" style={{color:colors.text,fontSize:isMobile?11:isTablet?11.5:13}}>{s.name}</div>
              </div>
            );
          })}
         </div>
        </div>
        )}

        {/* Col 1: Courses — hot loaded */}
        <div className="gx-col" style={{width:(100/COL_COUNT)+'%',opacity:viewDepth>=COL.courses?1:0,pointerEvents:viewDepth>=COL.courses?'auto':'none'}}>
         <div className="gx-colinner" style={{gap:gap}}>
          {/* SOURCE SWITCH. With the subject level on, this column is scoped to the
              selected subject (crsCache). With it off, it is every course from every
              subject, and the medallion label carries the subject name so the
              grouping is still visible. */}
          {(SHOW_SUBJECT_LEVEL
            ? crsCache.current.map(function(c){ return { subject: subD, course: c }; })
            : allCourses
          ).map(function(entry,i){
            var c = entry.course; var su = entry.subject;
            var sel=selCrs===c.id; var dim=selCrs&&!sel;
            var locked=isLocked(c.tierRequired, currentTier);
            var cPct=nodePct(c, progressByVideo); var cDone=nodeCompleted(c, progressByVideo);
            var oc=orbColors[i%4]; var og=orbGlows[i%4];
            return (
              <div key={c.id} className={'gx-med'+(sel?' gx-med--sel':'')+(cDone?' gx-med--done':'')}
                onClick={function(e){e.stopPropagation();
                  // Collapsing back to the course list means /courses when the subject
                  // level is hidden (there is no subject-only view to return to).
                  navigate(selCrs===c.id
                    ? (SHOW_SUBJECT_LEVEL ? coursesPath([su]) : '/courses')
                    : coursesPath([su,c]));}}
                style={{opacity:dim?0.22:1,cursor:'pointer',animationDelay:(60+i*70)+'ms'}}>
                {/* The circle is the same material as a subject orb, one step smaller.
                    The name sits BESIDE it because a course title will not fit inside. */}
                <div className="gx-med__orb" style={{background:'radial-gradient(circle at 35% 32%,'+oc+'22,'+oc+'0a,transparent 72%)',borderColor:sel?ac:(isLight?'rgba(0,0,0,0.07)':ac+'20'),boxShadow:sel?'0 0 26px '+gl:'none'}}>
                  <div className="gx-med__glow" style={{background:og}}/>
                  <Ring size={medSize} pct={cPct} color={oc} track={isLight?'rgba(0,0,0,0.05)':ac+'14'} width={2}/>
                  <span className="gx-med__num" style={{color:oc}}>{roman(i+1)}</span>
                </div>
                <div className="gx-med__meta">
                  <div className="gx-med__label" style={{color:colors.muted}}>
                    {locked ? 'Locked'
                      : cDone ? 'Complete'
                      : cPct > 0 ? cPct + '% complete'
                      : (SHOW_SUBJECT_LEVEL ? 'Course ' + (i+1) : (su && su.name) || 'Course')}
                  </div>
                  <div className="gx-med__name" style={{color:colors.text}}>{c.name}</div>
                </div>
                {locked
                  ? <span className="gx-med__mark">{lockMark(colors.muted)}</span>
                  : (cDone && <span className="gx-med__mark">{tick(ac,12)}</span>)}
              </div>
            );
          })}
         </div>
        </div>

        {/* Col 2: Chapters — hot loaded */}
        <div className="gx-col" style={{width:(100/COL_COUNT)+'%',opacity:viewDepth>=COL.chapters?1:0,pointerEvents:viewDepth>=COL.chapters?'auto':'none'}}>
         <div className="gx-colinner" style={{gap:gap}}>
          {/* ORBITAL SPINE. A single luminous hairline runs the height of the column
              and every chapter is a node sitting ON it. This is what carries the
              celestial language down to a level whose labels are 30+ characters:
              the circle stays, the text moves beside it, and the thread makes the
              sequence legible at a glance in a way a stack of rectangles never was. */}
          <div className="gx-spine" style={{'--gx-node':nodeSize+'px'}}>
            <div className="gx-spine__line" style={{background:'linear-gradient(to bottom,transparent,'+(isLight?'rgba(0,0,0,0.10)':ac+'2a')+' 12%,'+(isLight?'rgba(0,0,0,0.10)':ac+'2a')+' 88%,transparent)'}}/>
            {chCache.current.map(function(c,i){
              var sel=selCh===c.id; var dim=selCh&&!sel;
              var cPct=nodePct(c, progressByVideo); var cDone=nodeCompleted(c, progressByVideo);
              var oc=orbColors[i%4];
              return (
                <div key={c.id} className={'gx-node'+(sel?' gx-node--sel':'')+(cDone?' gx-node--done':'')}
                  onClick={function(e){e.stopPropagation();navigate(selCh===c.id?coursesPath([subD,crsD]):coursesPath([subD,crsD,c]))}}
                  style={{opacity:dim?0.24:1,animationDelay:(70+i*55)+'ms'}}>
                  <div className="gx-node__orb" style={{background:'radial-gradient(circle at 35% 32%,'+oc+'26,'+oc+'0c,transparent 74%)',borderColor:sel?ac:(isLight?'rgba(0,0,0,0.08)':ac+'22'),boxShadow:sel?'0 0 18px '+gl:'none'}}>
                    <Ring size={nodeSize} pct={cPct} color={oc} track={isLight?'rgba(0,0,0,0.05)':ac+'12'} width={1.75}/>
                    {cDone&&<span className="gx-node__chk">{tick(ac,11)}</span>}
                  </div>
                  <div className="gx-node__meta">
                    <div className="gx-node__label" style={{color:colors.muted,display:'flex',alignItems:'center',gap:5}}>Chapter {i+1}{isLocked(c.tierRequired, currentTier)&&lockMark(colors.muted)}</div>
                    <div className="gx-node__name" style={{color:colors.text}}>{c.name}</div>
                  </div>
                </div>
              );
            })}
          </div>
         </div>
        </div>

        {/* Col 3: Sections — hot loaded */}
        <div className="gx-col" style={{width:(100/COL_COUNT)+'%',opacity:viewDepth>=COL.sections?1:0,pointerEvents:viewDepth>=COL.sections?'auto':'none'}}>
         <div className="gx-colinner" style={{gap:gap}}>
          <div className="gx-spine gx-spine--sub" style={{'--gx-node':subNodeSize+'px'}}>
            <div className="gx-spine__line" style={{background:'linear-gradient(to bottom,transparent,'+(isLight?'rgba(0,0,0,0.08)':ac+'20')+' 14%,'+(isLight?'rgba(0,0,0,0.08)':ac+'20')+' 86%,transparent)'}}/>
            {secCache.current.map(function(sec,i){
              var sel=selSec===sec.id; var dim=selSec&&!sel;
              var sPct2=nodePct(sec, progressByVideo); var secDone=nodeCompleted(sec, progressByVideo);
              var oc=orbColors[i%4];
              return (
                <div key={sec.id} className={'gx-node'+(sel?' gx-node--sel':'')+(secDone?' gx-node--done':'')}
                  onClick={function(e){e.stopPropagation();navigate(selSec===sec.id?coursesPath([subD,crsD,chD]):coursesPath([subD,crsD,chD,sec]))}}
                  style={{opacity:dim?0.26:1,animationDelay:(80+i*50)+'ms'}}>
                  <div className="gx-node__orb" style={{background:'radial-gradient(circle at 35% 32%,'+oc+'22,'+oc+'0a,transparent 74%)',borderColor:sel?ac:(isLight?'rgba(0,0,0,0.07)':ac+'1c'),boxShadow:sel?'0 0 14px '+gl:'none'}}>
                    <Ring size={subNodeSize} pct={sPct2} color={oc} track={isLight?'rgba(0,0,0,0.04)':ac+'10'} width={1.5}/>
                    {secDone&&<span className="gx-node__chk">{tick(ac,9)}</span>}
                  </div>
                  <div className="gx-node__meta">
                    <div className="gx-node__label" style={{color:colors.muted,display:'flex',alignItems:'center',gap:5}}>Section {i+1}{isLocked(sec.tierRequired, currentTier)&&lockMark(colors.muted)}</div>
                    <div className="gx-node__name" style={{color:colors.textSecondary}}>{sec.name}</div>
                  </div>
                </div>
              );
            })}
          </div>
         </div>
        </div>

        {/* Col 4: Items — hot loaded */}
        <div className="gx-col" style={{width:(100/COL_COUNT)+'%',opacity:viewDepth>=COL.topics?1:0,pointerEvents:viewDepth>=COL.topics?'auto':'none'}}>
         <div className="gx-colinner" style={{gap:gap}}>
          {itmCache.current&&(
            <div className="gx-items">
              <div className="gx-items__head" style={{background:panelBg,borderColor:panelBd,color:colors.text}}>{itmCache.current.name}</div>
              <div className="gx-items__body" style={{background:panelBg,borderColor:panelBd}}>
                {(itmCache.current.topics||[]).map(function(t){
                  var done=videoCompleted(progressByVideo, t.videoId); var locked=isLocked(t.tierRequired, currentTier);
                  return (
                    <div key={t.id} className={'gx-items__link'+(done?' gx-items__link--done':'')}
                      onClick={function(){if(locked){setUpgradeOpen(true);return;}navigate('/video/'+t.videoId+(selCrs?'?course='+selCrs:''));}}
                      style={{cursor:'pointer',color:colors.textSecondary,borderColor:panelBd,borderLeft:done?'2.5px solid '+doneBorder:'2.5px solid transparent',background:done?doneTint:'transparent'}}>
                      {/* Satellite token: a mini progress ring with the play glyph inside,
                          so a topic is visibly the smallest member of the same family. */}
                      <span className="gx-items__tok">
                        <Ring size={tokSize} pct={videoPct(progressByVideo, t.videoId)} color={ac} track={isLight?'rgba(0,0,0,0.07)':ac+'20'} width={1.5}/>
                        {/* SVG, not the '\u25B6' character. iOS renders U+25B6 with its
                            COLOUR EMOJI font, so on mobile the token showed a boxy
                            coloured triangle at the wrong size and baseline while desktop
                            drew plain type — which is why it only looked wrong on a phone.
                            A path has fixed geometry, takes the tier colour, and centres
                            exactly on every platform. */}
                        <svg className="gx-items__ico" width={triSize} height={triSize}
                          viewBox="0 0 10 10" fill={ac} aria-hidden="true">
                          <path d="M3.4 2.2 L7.6 5 L3.4 7.8 Z"/>
                        </svg>
                      </span>
                      <span className="gx-items__txt" style={{opacity:done?0.95:0.8}}>{t.name}</span>
                      {locked
                        ?<span className="gx-items__tag" style={{background:tagBg,color:colors.muted,display:'inline-flex',alignItems:'center',gap:4}}>{lockMark(colors.muted)}Locked</span>
                        :(done&&<span className="gx-items__chk">{tick(ac,11)}</span>)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
         </div>
        </div>
      </div>

      {/* ── Left back navigation ── */}
      {/* viewDepth, not depth: with the subject level hidden there is no rung above
          the course list, so no rail is offered there. */}
      {viewDepth>0&&(
        <div className="gx-backnav" onClick={goBack}
          style={{background:isLight?colors.bg+'e0':colors.bg+'cc',borderRight:'0.5px solid '+(isLight?'rgba(0,0,0,0.06)':ac+'15')}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isLight?colors.textSecondary:ac} strokeWidth="1.5">
            <path d="M19 12H5" strokeLinecap="round"/>
            <path d="M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="gx-backnav__label" style={{color:isLight?colors.textSecondary:ac}}>{backLabel}</span>
        </div>
      )}

      {/* ── HUD ── */}
      <div className="gx-hud">
        {crumbs&&(
          <div className="gx-crumbs" style={{color:colors.muted}}>
            {crumbs.map(function(c,i){return(
              <React.Fragment key={i}>
                {i>0&&<span className="gx-crumbs__sep">{'\u203A'}</span>}
                <span className={'gx-crumbs__item'+(c.cur?' gx-crumbs__item--cur':'')} onClick={c.cur?undefined:c.fn}>{c.l}</span>
              </React.Fragment>
            )})}
          </div>
        )}
      </div>

      {loading&&<div className="gx-state"><div className="gx-spinner" style={{borderColor:ac+'30',borderTopColor:ac}}/><div style={{fontFamily:"'Outfit',sans-serif",fontSize:12,fontWeight:300,color:colors.muted}}>Loading...</div></div>}
      {!loading&&(SHOW_SUBJECT_LEVEL?subjects.length===0:allCourses.length===0)&&<div className="gx-state"><div style={{fontFamily:"'Cormorant',serif",fontSize:isMobile?22:28,fontWeight:300,color:colors.text}}>{SHOW_SUBJECT_LEVEL?'No subjects yet':'No courses yet'}</div><div style={{fontFamily:"'Outfit',sans-serif",fontSize:isMobile?11:13,fontWeight:300,color:colors.muted,maxWidth:320,textAlign:'center',lineHeight:1.6}}>{SHOW_SUBJECT_LEVEL?'Your enrolled subjects will appear here as celestial bodies.':'Your enrolled courses will appear here as celestial bodies.'}</div></div>}
      <UpgradePrompt open={upgradeOpen} onClose={function(){setUpgradeOpen(false);}} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant:wght@300;400;500&family=Outfit:wght@200;300;400;500&display=swap');
        /* 100dvh tracks the LIVE visible height. 100vh on iOS is the height with the browser
           chrome COLLAPSED, so the bottom sat under the URL bar. The duplicate declaration
           is a deliberate cascade fallback for browsers without dvh. */
        .gx{width:100%;height:100vh;height:100dvh;position:relative;overflow:hidden}
        .gx-track{position:absolute;top:0;left:0;bottom:0;display:flex;align-items:stretch;transition:transform 1.1s cubic-bezier(0.4,0,0.15,1);will-change:transform;z-index:2}
        /* SCROLLABLE. Was justify-content:center with no overflow-y, inside a parent with
           overflow:hidden — so a column taller than the viewport was clipped at BOTH ends
           with no way to reach the hidden items. Today's catalog only just fits (9 chapters
           need ~512px; an iPhone SE has ~587px usable), a data-dependent cliff with no
           warning. flex-start + margin:auto on .gx-colinner keeps the centred look when
           content fits and scrolls when it does not. */
        /* --gx-gutter is the width the back rail occupies. It is applied as EXTRA
           left padding on every column so the rail sits in reserved space instead of
           on top of the cards. Previously the rail was position:absolute over the
           track with only 16px of column padding, so on mobile it covered the left
           28px of every card — the progress dot and the first characters of every
           name — and on tablet the centred 340px card started at x~29 and went under
           it as well. The rail's hover expansion is included in the reserve so the
           layout never shifts. */
        .gx-col{flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-start;align-items:center;
          padding-top:${isMobile?'104px':'40px'};   /* clears the hamburger row + crumb line */padding-bottom:${isMobile?'28px':'40px'};
          padding-right:${isMobile?'16px':'24px'};
          padding-left:calc(${isMobile?'16px':'24px'} + var(--gx-gutter, 0px));
          box-sizing:border-box;transition:opacity .5s ease,padding-left .35s ease;
          overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain}
        .gx-col::-webkit-scrollbar{width:0;height:0}
        .gx-colinner{display:flex;flex-direction:column;align-items:center;width:100%;margin:auto 0;flex-shrink:0}

        /* Orbs */
        @keyframes gx-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-${isMobile?5:8}px)}}
        @keyframes gx-pulse{0%,100%{transform:scale(1);opacity:.12}50%{transform:scale(1.05);opacity:.2}}
        .gx-orb{border-radius:50%;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;flex-shrink:0;animation:gx-bob 3.5s ease-in-out infinite;transition:opacity .6s,filter .3s,transform .3s}
        @media (hover:hover) and (pointer:fine){ .gx-orb:hover{filter:brightness(1.15);transform:scale(1.05)} }
        .gx-orb__glow{position:absolute;inset:-45%;border-radius:50%;filter:blur(${isMobile?20:30}px);opacity:.28;transition:opacity .7s;pointer-events:none}
        .gx-orb:hover .gx-orb__glow,.gx-orb--sel .gx-orb__glow{opacity:.55}
        .gx-orb__ring{position:absolute;inset:-${isMobile?6:9}px;border-radius:50%;border:.5px solid;opacity:.12;animation:gx-pulse 4.5s ease-in-out infinite}
        .gx-orb--done .gx-orb__ring{opacity:.35}
        .gx-orb__check{position:absolute;top:-2px;right:-2px;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;z-index:3}
        .gx-orb__sym{display:block;margin-bottom:${isMobile?1:3}px;opacity:.75;position:relative;z-index:1}
        .gx-orb__name{font-family:'Cormorant',serif;font-weight:400;white-space:nowrap;position:relative;z-index:1}

        /* Cards */
        @keyframes gx-cardIn{from{opacity:0;transform:translateX(-16px) scale(.92)}to{opacity:1;transform:translateX(0) scale(1)}}

        /* ═══════════════════════════════════════════════════════════════
           SHARED MATERIAL — every level is made of the same substance
           ═══════════════════════════════════════════════════════════════ */
        .gx-arc{position:absolute;inset:0;pointer-events:none;overflow:visible}
        .gx-arc circle{transition:stroke-dashoffset 1.1s cubic-bezier(0.4,0,0.15,1)}

        /* ── LEVEL 1: medallions ─────────────────────────────────────── */
        @keyframes gx-medIn{from{opacity:0;transform:translateX(-14px)}to{opacity:1;transform:translateX(0)}}
        .gx-med{display:flex;align-items:center;gap:${isMobile?14:16}px;width:100%;
          max-width:${isMobile?440:340}px;min-height:${medSize}px;flex-shrink:0;
          animation:gx-medIn .55s cubic-bezier(0.4,0,0.15,1) both;
          transition:transform .35s cubic-bezier(0.4,0,0.15,1),opacity .5s}
        @media (hover:hover) and (pointer:fine){ .gx-med:hover{transform:translateX(4px)} }
        .gx-med__orb{position:relative;width:${medSize}px;height:${medSize}px;flex-shrink:0;
          border-radius:50%;border:.5px solid;display:flex;align-items:center;justify-content:center;
          backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
          transition:box-shadow .4s,border-color .4s}
        .gx-med__glow{position:absolute;inset:-30%;border-radius:50%;filter:blur(16px);opacity:.5;pointer-events:none}
        .gx-med__num{position:relative;z-index:1;font-family:'Cormorant',serif;font-weight:300;
          font-size:${isMobile?17:20}px;letter-spacing:.04em}
        .gx-med__meta{flex:1;min-width:0}
        .gx-med__label{font-family:'Outfit',sans-serif;font-weight:500;font-size:${isMobile?9:9}px;
          letter-spacing:.1em;text-transform:uppercase;opacity:.55;margin-bottom:3px;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .gx-med__name{font-family:'Cormorant',serif;font-weight:400;font-size:${isMobile?17:18}px;
          line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .gx-med__mark{flex-shrink:0;opacity:.7;display:inline-flex;align-items:center;line-height:0}

        /* ── LEVELS 2-3: the orbital spine ───────────────────────────── */
        /* One hairline runs the height of the stack; nodes sit ON it. The line is a
           child rather than a ::before so its gradient can follow the theme. */
        .gx-spine{position:relative;width:100%;max-width:${isMobile?440:340}px;
          display:flex;flex-direction:column;gap:${isMobile?4:6}px}
        .gx-spine__line{position:absolute;top:0;bottom:0;left:calc(var(--gx-node) / 2);
          width:1px;transform:translateX(-.5px);pointer-events:none}
        @keyframes gx-nodeIn{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:translateX(0)}}
        .gx-node{position:relative;display:flex;align-items:center;gap:${isMobile?12:14}px;
          min-height:48px;cursor:pointer;flex-shrink:0;
          animation:gx-nodeIn .5s cubic-bezier(0.4,0,0.15,1) both;
          transition:transform .3s cubic-bezier(0.4,0,0.15,1),opacity .5s}
        @media (hover:hover) and (pointer:fine){ .gx-node:hover{transform:translateX(3px)} }
        .gx-node__orb{position:relative;width:var(--gx-node);height:var(--gx-node);flex-shrink:0;
          border-radius:50%;border:.5px solid;display:flex;align-items:center;justify-content:center;
          backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
          transition:box-shadow .35s,border-color .35s}
        .gx-node__chk{position:relative;z-index:1;display:inline-flex;line-height:0}
        .gx-node__meta{flex:1;min-width:0;padding:2px 0}
        .gx-node__label{font-family:'Outfit',sans-serif;font-weight:500;font-size:8.5px;
          letter-spacing:.1em;text-transform:uppercase;opacity:.5;
          white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .gx-node__name{font-family:'Cormorant',serif;font-weight:400;font-size:${isMobile?15:15.5}px;
          line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .gx-spine--sub .gx-node__name{font-size:${isMobile?13.5:14}px}

        /* ── LEVEL 4: satellite tokens ───────────────────────────────── */
        .gx-items__tok{position:relative;width:${tokSize}px;height:${tokSize}px;flex-shrink:0;
          display:inline-flex;align-items:center;justify-content:center}
        .gx-items__tok .gx-items__ico{position:relative;z-index:1;opacity:.9;display:block}

        /* Items */
        @keyframes gx-panelIn{from{opacity:0;transform:translateX(-12px) scale(.94)}to{opacity:1;transform:translateX(0) scale(1)}}
        .gx-items{animation:gx-panelIn .5s ease 80ms both;width:100%;max-width:${isMobile?440:340}px}
        .gx-items__head{font-family:'Outfit',sans-serif;font-weight:500;padding:${isMobile?'12px 14px':'11px 15px'};font-size:${isMobile?12:11}px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;border-radius:${isMobile?7:9}px ${isMobile?7:9}px 0 0;border:.5px solid;border-bottom:none;backdrop-filter:blur(10px)}
        .gx-items__body{border-radius:0 0 ${isMobile?7:9}px ${isMobile?7:9}px;border:.5px solid;border-top:none;overflow:hidden;backdrop-filter:blur(10px)}
        /* min-height:48px — these are the primary controls of the page and were ~27px. */
        .gx-items__link{display:flex;align-items:center;gap:8px;font-family:'Outfit',sans-serif;font-weight:300;
          text-decoration:none;padding:${isMobile?'10px 14px':'10px 15px'};font-size:${isMobile?12.5:10.5}px;
          min-height:48px;box-sizing:border-box;transition:background .25s,opacity .25s;cursor:pointer;border-top:.5px solid}
        .gx-items__link:first-child{border-top:none}
        /* Hover fires on TAP on touch devices, and animating padding-left slid the label out
           from under the finger mid-tap. Restricted to real pointers. */
        @media (hover:hover) and (pointer:fine){
          .gx-items__link:hover{padding-left:20px;background:${hoverBg}}
        }
        .gx-items__ico{flex-shrink:0}
        .gx-items__txt{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .gx-items__chk{display:inline-flex;line-height:0;opacity:.75;margin-right:4px;flex-shrink:0}
        .gx-items__tag{font-family:'Outfit',sans-serif;font-weight:500;font-size:${isMobile?7:8}px;letter-spacing:.05em;text-transform:uppercase;padding:2px 6px;border-radius:3px;opacity:.55;flex-shrink:0}

        /* Left back nav */
        .gx-backnav{position:absolute;left:0;top:0;bottom:0;width:${isMobile?44:56}px;z-index:8;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;cursor:pointer;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);transition:opacity .4s,width .3s}
        .gx-backnav:hover{width:${isMobile?52:72}px}
        .gx-backnav__label{font-family:'Outfit',sans-serif;font-size:9px;font-weight:300;letter-spacing:.06em;writing-mode:vertical-lr;transform:rotate(180deg);opacity:.5;transition:opacity .3s}
        .gx-backnav:hover .gx-backnav__label{opacity:.9}

        /* HUD */
        .gx-hud{position:absolute;inset:0;z-index:5;pointer-events:none}
        /* On mobile the crumbs used to start at x=50 while AppLayout's fixed hamburger
           occupies x=16..60 / y=16..60 at zIndex 997 — so the hamburger sat on top and
           covered the first crumb. Mobile now drops the crumbs BELOW that button and
           clears the back rail horizontally; desktop keeps them on the top line. */
        .gx-crumbs{position:absolute;top:${isMobile?'68px':'18px'};left:calc(${isMobile?'16px':'70px'} + var(--gx-gutter, 0px));right:${isMobile?'16px':'auto'};
          flex-wrap:wrap;display:flex;align-items:center;gap:${isMobile?4:6}px;font-family:'Outfit',sans-serif;font-size:${isMobile?9:10}px;font-weight:300;letter-spacing:.06em;pointer-events:auto;flex-wrap:wrap}
        .gx-crumbs__sep{opacity:.25;font-size:${isMobile?7:8}px}
        .gx-crumbs__item{cursor:pointer;opacity:.4;transition:opacity .3s}
        .gx-crumbs__item:hover{opacity:1}
        .gx-crumbs__item--cur{cursor:default;opacity:.8}

        .gx-state{position:absolute;inset:0;z-index:10;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;pointer-events:none}
        @keyframes gx-spin{to{transform:rotate(360deg)}}
        .gx-spinner{width:32px;height:32px;border:2px solid;border-radius:50%;animation:gx-spin 1s linear infinite}
      `}</style>
    </div>
  );
}
